import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import { SortingOrderE } from 'declarations/enums/common';
import { FILTER_PREFERENCES_VERSION, StorageE } from 'declarations/enums/storage';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import {
  AgeFilterValuesE,
  AgentRoleFilterValuesE,
  AssigneeFilterValuesE,
  ParameterSortingValuesE,
  ScheduleFilterValuesE,
  StatusFilterValuesE,
  TicketsRefinementKeysE,
} from 'declarations/enums/tickets';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { getCurrentUserId } from 'helpers/user';

type UseTicketsRefinementT = {
  isOpenStatusFilterActive: boolean;
  isCloseStatusFilterActive: boolean;
  isUnder24HoursFilterActive: boolean;
  isOver24HoursFilterActive: boolean;
  isAssignedToMeFilterActive: boolean;
  isPastMonthFilterActive: boolean;
  isPast30DaysFilterActive: boolean;
  isArchivedFilterActive: boolean;
  isMyPrimaryFilterActive: boolean;
  isMyBackUpFilterActive: boolean;
  isFiltersPopupOpened: boolean;
  isRONumberSortingActive: boolean;
  isLineNumberSortingActive: boolean;
  isAccountNumberSortingActive: boolean;
  isAgeSortingActive: boolean;
  isClaimAgeSortingActive: boolean;
  isCloseROAgeSortingActive: boolean;
  isStatusSortingActive: boolean;
  isAssigneeSortingActive: boolean;
  isCategorySortingActive: boolean;
  isSubCategorySortingActive: boolean;
  isAscSortingActive: boolean;
  isDescSortingActive: boolean;
  isAmountSortingActive: boolean;
  isTechSortingActive: boolean;
  isAdvisorSortingActive: boolean;
  isVinSortingActive: boolean;
  isApprovedByDealerSortingActive: boolean;
  isLastCommentSortingActive: boolean;
  isAuditTrailSortingActive: boolean;
  hasAttachmentSortingActive: boolean;
  isAssignToDealerFilterActive: boolean;
  isAssignToWarrCloudFilterActive: boolean;
  isAssignToOEMFilterActive: boolean;
  isClaimStatusSortingActive: boolean;
  isTicketPrioritySortingActive: boolean;
  isLessThan30DaysFilterActive: boolean;
  isFrom30To59DaysFilterActive: boolean;
  isFrom60To89DaysFilterActive: boolean;
  isFrom90To119DaysFilterActive: boolean;
  isOver120DaysFilterActive: boolean;
  isScheduleAccountsFilterActive: boolean;
  sortOptions: SelectOptionT[];
  scheduleAgeOptions: SelectOptionT[];
  sortSelectValue: string;
  selectedAgeFilter: string;
  handleUpdateFilterClick: (key: TicketsRefinementKeysE, value: string) => NoopT;
  handleUpdateSortingClick: (key: TicketsRefinementKeysE, value: string) => NoopT;
  handleOrderSortSelectClick: (key: TicketsRefinementKeysE, value: SortingOrderE) => NoopT;
  handleUpdateSortSelectClick: (value: string) => void;
  handleUpdateScheduleSelectClick: (value: string) => void;
  handleScheduleAgeFilterChange: (event: string | null) => void;
  handleClearAllFiltersClick: NoopT;
  handleOpenFiltersPopup: NoopT;
  handleCloseFiltersPopup: NoopT;
  selectKey: number;
  filtersReset: number;
  isSelectAllTicketsChecked: boolean;
  handleSelectAllTicketsClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setIsSelectAllTicketsChecked: (all: boolean) => void;
};

const useTicketsRefinement = (): UseTicketsRefinementT => {
  /**
   * Hooks
   */
  const {
    getSearchParam,
    updateSearchParam,
    clearSearchParam,
    ticketsCount,
    setActiveScheduleCards,
    setActiveScheduleFilters,
    setSelectedTicketsIds,
    setDealerSelectedRooftopIds,
    ticketsPages,
    activeTab,
    setIsScheduleTicketList,
    setSelectedSchedule,
  } = useTickets();
  const [isFiltersPopupOpened, setFiltersPopupVisibility] = useState(false);
  const [sortSelectValue, setSortSelectValue] = useState('');
  const [selectedAgeFilter, setSelectedAgeFilter] = useState('');
  const [selectKey, setSelectKey] = useState(0);
  const [filtersReset, setFiltersReset] = useState(0);
  const [isSelectAllTicketsChecked, setIsSelectAllTicketsChecked] = useState(false);

  /**
   * Variables
   */
  const statusFilter = getSearchParam(TicketsRefinementKeysE.Status);
  const agentRoleFilter = getSearchParam(TicketsRefinementKeysE.AgentRoofTopRole);
  const pastMonthFilter = getSearchParam(TicketsRefinementKeysE.PastMonth);
  const past30DaysFilter = getSearchParam(TicketsRefinementKeysE.Past30Days);
  const archivedFilter = getSearchParam(TicketsRefinementKeysE.IsArchived);
  const ageFilter = getSearchParam(TicketsRefinementKeysE.TicketAge);
  const assignedToMeFilter = getSearchParam(TicketsRefinementKeysE.AssignedToMe);
  const sortingParameter = getSearchParam(TicketsRefinementKeysE.Parameter);
  const scheduleAgeParameter = getSearchParam(TicketsRefinementKeysE.Schedule);
  const sortingOrder = getSearchParam(TicketsRefinementKeysE.Order);
  const assigneeFilter = getSearchParam(TicketsRefinementKeysE.Assignee);

  const isOpenStatusFilterActive = !!statusFilter && statusFilter === StatusFilterValuesE.Open;
  const isCloseStatusFilterActive = !!statusFilter && statusFilter === StatusFilterValuesE.Closed;
  const isPastMonthFilterActive = !!pastMonthFilter && pastMonthFilter === 'true';
  const isPast30DaysFilterActive = !!past30DaysFilter && past30DaysFilter === 'true';
  const isArchivedFilterActive = !!archivedFilter && archivedFilter === 'true';
  const isUnder24HoursFilterActive = !!ageFilter && ageFilter === AgeFilterValuesE.Under24Hours;
  const isOver24HoursFilterActive = !!ageFilter && ageFilter === AgeFilterValuesE.Over24Hours;
  const isAssignedToMeFilterActive = !!assignedToMeFilter && assignedToMeFilter === 'true';
  const isMyPrimaryFilterActive =
    !!agentRoleFilter && agentRoleFilter === AgentRoleFilterValuesE.Primary;
  const isMyBackUpFilterActive =
    !!agentRoleFilter && agentRoleFilter === AgentRoleFilterValuesE.BackUp;
  const isRONumberSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.RONumber;
  const isLineNumberSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.LineNumber;
  const isAgeSortingActive = !!sortingParameter && sortingParameter === ParameterSortingValuesE.Age;
  const isClaimAgeSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.ClaimAge;
  const isCloseROAgeSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.ClosedROAge;
  const isStatusSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.Status;
  const isAssigneeSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.Assignee;
  const isCategorySortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.RequestCategory;
  const isAmountSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.Amount;
  const isApprovedByDealerSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.IsApprovedByDealer;
  const hasAttachmentSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.HasAttachment;
  const isClaimStatusSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.ClaimStatus;
  const isTicketPrioritySortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.TicketPriority;
  const isAdvisorSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.Advisor;
  const isVinSortingActive = !!sortingParameter && sortingParameter === ParameterSortingValuesE.Vin;
  const isTechSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.Tech;
  const isSubCategorySortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.SubCategory;
  const isLastCommentSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.LastComment;
  const isAuditTrailSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.AuditTrail;
  const isLessThan30DaysFilterActive =
    !!scheduleAgeParameter && scheduleAgeParameter === ScheduleFilterValuesE.LessThan31Days;
  const isFrom30To59DaysFilterActive =
    !!scheduleAgeParameter && scheduleAgeParameter === ScheduleFilterValuesE.From31To60Days;
  const isFrom60To89DaysFilterActive =
    !!scheduleAgeParameter && scheduleAgeParameter === ScheduleFilterValuesE.From61To90Days;
  const isFrom90To119DaysFilterActive =
    !!scheduleAgeParameter && scheduleAgeParameter === ScheduleFilterValuesE.From91To120Days;
  const isOver120DaysFilterActive =
    !!scheduleAgeParameter && scheduleAgeParameter === ScheduleFilterValuesE.Over120Days;
  const isAscSortingActive = !!sortingOrder && sortingOrder === SortingOrderE.Asc;
  const isDescSortingActive = !!sortingOrder && sortingOrder === SortingOrderE.Desc;
  const isAccountNumberSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.DMSScheduleAccountNumber;
  const isScheduleAccountsFilterActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.DMSScheduleAccounts;
  const isAssignToDealerFilterActive =
    !!assigneeFilter && assigneeFilter === AssigneeFilterValuesE.Dealer;
  const isAssignToWarrCloudFilterActive =
    !!assigneeFilter && assigneeFilter === AssigneeFilterValuesE.WarrCloud;
  const isAssignToOEMFilterActive =
    !!assigneeFilter && assigneeFilter === AssigneeFilterValuesE.OEM;
  const sortOptions = useMemo(
    () => [
      {
        label: 'Ticket Age',
        value: `${ParameterSortingValuesE.Age}`,
      },
      {
        label: 'Closed RO Age',
        value: `${ParameterSortingValuesE.ClosedROAge}`,
      },
      {
        label: 'RO number',
        value: `${ParameterSortingValuesE.RONumber}`,
      },
      {
        label: 'Line number',
        value: `${ParameterSortingValuesE.LineNumber}`,
      },
      {
        label: 'Priority',
        value: `${ParameterSortingValuesE.TicketPriority}`,
      },
      {
        label: 'Ticket Status',
        value: `${ParameterSortingValuesE.Status}`,
      },
      {
        label: 'Ticket Assignee',
        value: `${ParameterSortingValuesE.Assignee}`,
      },
      {
        label: 'Request Category',
        value: `${ParameterSortingValuesE.RequestCategory}`,
      },
      {
        label: 'Account',
        value: `${ParameterSortingValuesE.DMSScheduleAccountNumber}`,
      },
      {
        label: 'Request Subcategory',
        value: `${ParameterSortingValuesE.RequestSubcategory}`,
      },
      {
        label: 'Ticket Amount',
        value: `${ParameterSortingValuesE.Amount}`,
      },
      {
        label: 'Claim Status',
        value: `${ParameterSortingValuesE.ClaimStatus}`,
      },
      {
        label: 'Attachment',
        value: `${ParameterSortingValuesE.HasAttachment}`,
      },
      {
        label: 'Approved',
        value: `${ParameterSortingValuesE.IsApprovedByDealer}`,
      },
      {
        label: 'Tech Number',
        value: `${ParameterSortingValuesE.Tech}`,
      },
      {
        label: 'Advisor Number',
        value: `${ParameterSortingValuesE.Advisor}`,
      },
      {
        label: 'VIN',
        value: `${ParameterSortingValuesE.Vin}`,
      },
      {
        label: 'Last Comment',
        value: `${ParameterSortingValuesE.LastComment}`,
      },
      {
        label: 'Audit Trail',
        value: `${ParameterSortingValuesE.AuditTrail}`,
      },
    ],
    []
  );
  const scheduleAgeOptions = useMemo(
    () => [
      {
        label: '-',
        value: `${ScheduleFilterValuesE.Undefined}`,
      },
      {
        label: '0-30 days',
        value: `${ScheduleFilterValuesE.LessThan31Days}`,
      },
      {
        label: '31-60 days',
        value: `${ScheduleFilterValuesE.From31To60Days}`,
      },
      {
        label: '61-90 days',
        value: `${ScheduleFilterValuesE.From61To90Days}`,
      },
      {
        label: '91-120 days',
        value: `${ScheduleFilterValuesE.From91To120Days}`,
      },
      {
        label: '121+',
        value: `${ScheduleFilterValuesE.Over120Days}`,
      },
    ],
    []
  );

  /**
   * Handlers
   */
  const handleUpdateFilterClick = useCallback(
    (key: TicketsRefinementKeysE, value: string) => () => {
      const filterValue = getSearchParam(key);
      const isFilterSelected = filterValue && filterValue === value;
      const isArchivedFilterClicked = key === TicketsRefinementKeysE.IsArchived;

      const isPastMonthFilterActive = getSearchParam(TicketsRefinementKeysE.PastMonth) === 'true';

      if (
        isPastMonthFilterActive &&
        (key === TicketsRefinementKeysE.TicketAge || key === TicketsRefinementKeysE.Past30Days)
      )
        clearSearchParam(TicketsRefinementKeysE.PastMonth);

      if (isArchivedFilterClicked) {
        clearSearchParam([
          TicketsRefinementKeysE.AssignedToMe,
          TicketsRefinementKeysE.Status,
          TicketsRefinementKeysE.TicketAge,
          TicketsRefinementKeysE.PastMonth,
          TicketsRefinementKeysE.Past30Days,
        ]);
      } else if (!isFilterSelected) {
        clearSearchParam(TicketsRefinementKeysE.IsArchived);
      }

      if (isFilterSelected) {
        if (isArchivedFilterClicked) {
          updateSearchParam(key, 'false');
        } else {
          clearSearchParam(key);
        }
      } else {
        updateSearchParam(key, value);
      }
    },
    [clearSearchParam, updateSearchParam, getSearchParam]
  );

  const handleUpdateSortingClick = useCallback(
    (key: TicketsRefinementKeysE, value: string) => () => {
      const sortValue = getSearchParam(TicketsRefinementKeysE.Order);

      if (ticketsCount) {
        updateSearchParam(key, value);

        if (sortValue === SortingOrderE.Asc) {
          updateSearchParam(TicketsRefinementKeysE.Order, SortingOrderE.Desc);
        } else {
          updateSearchParam(TicketsRefinementKeysE.Order, SortingOrderE.Asc);
        }
      }
    },
    [updateSearchParam, getSearchParam, ticketsCount]
  );
  const handleUpdateSortSelectClick = useCallback(
    (value: string) => {
      updateSearchParam(TicketsRefinementKeysE.Parameter, value);
      setSortSelectValue(value);
    },
    [updateSearchParam]
  );
  const handleOrderSortSelectClick = useCallback(
    (key: TicketsRefinementKeysE, value: SortingOrderE) => () => {
      updateSearchParam(key, value);
    },
    [updateSearchParam]
  );
  const handleUpdateScheduleSelectClick = useCallback(
    (value: string) => {
      updateSearchParam(TicketsRefinementKeysE.Schedule, value.toString());
      setSelectedAgeFilter(value.toString());
    },
    [updateSearchParam]
  );

  const handleScheduleAgeFilterChange = useCallback(
    (option: string | null) => {
      if (option) {
        setSelectedAgeFilter(option.toString());
        updateSearchParam(TicketsRefinementKeysE.Schedule, option.toString());
      }
    },
    [updateSearchParam]
  );

  const handleClearAllFiltersClick = (): void => {
    // Keep rooftop/dealership selection intact (do not clear RoofTopId).
    // Keep search intact to match WarrQueue behavior (clear filters != clear search).
    setIsSelectAllTicketsChecked(false);
    setSelectKey(prevKey => prevKey + 1);
    setSortSelectValue('');
    setSelectedAgeFilter('');
    const shouldPreserveSchedule = activeTab === ActiveTabTypeE.Schedule;

    // Reset schedule file filter state so ScheduleFileId stays cleared (effect won't re-add it)
    setIsScheduleTicketList(false);
    setSelectedSchedule(null);

    clearSearchParam([
      TicketsRefinementKeysE.Status,
      TicketsRefinementKeysE.TicketAge,
      TicketsRefinementKeysE.AssignedToMe,
      TicketsRefinementKeysE.PastMonth,
      TicketsRefinementKeysE.Past30Days,
      TicketsRefinementKeysE.IsArchived,
      TicketsRefinementKeysE.AgentRoofTopRole,
      TicketsRefinementKeysE.Assignee,
      TicketsRefinementKeysE.Schedule,
      TicketsRefinementKeysE.SubCategory,
      TicketsRefinementKeysE.DMSScheduleAccounts,
      TicketsRefinementKeysE.ScheduleFileId,
      TicketsRefinementKeysE.RequestSubCategoryIds,
      TicketsRefinementKeysE.Parameter,
      TicketsRefinementKeysE.Order,
      TicketsRefinementKeysE.RoofTopId,
      ...(shouldPreserveSchedule ? [] : [TicketsRefinementKeysE.Category]),
    ]);

    // Re-apply defaults: My Work OFF, Open OFF, Dealers ALL (no re-apply needed; cleared params = defaults)
    setDealerSelectedRooftopIds(null);

    setFiltersReset(prev => prev + 1);
    setActiveScheduleCards([]);
    setActiveScheduleFilters([]);
  };

  const handleOpenFiltersPopup = (): void => {
    setFiltersPopupVisibility(true);
  };
  const handleCloseFiltersPopup = (): void => {
    setFiltersPopupVisibility(false);
  };

  const handleSelectAllTicketsClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const isChecked = event.target.checked;

    setIsSelectAllTicketsChecked(isChecked);

    const ticketIds: string[] =
      ticketsPages?.flatMap(page =>
        page.data?.map(({ id }) => id).filter((id): id is string => id !== null && id !== undefined)
      ) || [];

    if (isChecked) {
      setSelectedTicketsIds(ticketIds || []);
    } else {
      setSelectedTicketsIds([]);
      setIsSelectAllTicketsChecked(false);
    }
  };

  /**
   * Effects
   */
  useEffect(() => {
    const scheduleAgeFromQuery = getSearchParam(TicketsRefinementKeysE.Schedule);
    if (scheduleAgeFromQuery) {
      setSelectedAgeFilter(scheduleAgeFromQuery);
    }
  }, [getSearchParam]);

  useEffect((): void => {
    const status = getSearchParam(TicketsRefinementKeysE.Status);
    const assignedToMe = getSearchParam(TicketsRefinementKeysE.AssignedToMe);

    // If filters already exist in URL, don't apply defaults
    if (status || assignedToMe) return;

    // Check if user has saved preferences in localStorage
    const userId = getCurrentUserId();
    if (userId) {
      try {
        const stored = localStorage.getItem(`${StorageE.TicketFilterPreferences}${userId}`);
        if (stored) {
          const parsed = JSON.parse(stored) as {
            version?: number;
            params?: Record<string, string>;
          };
          // If valid saved preferences exist, let TicketsContext handle restoration
          if (parsed.version === FILTER_PREFERENCES_VERSION && parsed.params) {
            return;
          }
        }
      } catch {
        // Ignore invalid stored data, fall through to defaults
      }
    }

    // No saved preferences - apply defaults: My Work OFF, Open OFF, Dealers ALL (no params = defaults)
  }, [getSearchParam]);

  return {
    isOpenStatusFilterActive,
    isCloseStatusFilterActive,
    isUnder24HoursFilterActive,
    isOver24HoursFilterActive,
    isAssignedToMeFilterActive,
    isPastMonthFilterActive,
    isPast30DaysFilterActive,
    isArchivedFilterActive,
    isLessThan30DaysFilterActive,
    isFrom30To59DaysFilterActive,
    isFrom60To89DaysFilterActive,
    isFrom90To119DaysFilterActive,
    isOver120DaysFilterActive,
    isFiltersPopupOpened,
    isCloseROAgeSortingActive,
    isRONumberSortingActive,
    isLineNumberSortingActive,
    isAccountNumberSortingActive,
    isAscSortingActive,
    isStatusSortingActive,
    isDescSortingActive,
    isAssigneeSortingActive,
    isCategorySortingActive,
    isSubCategorySortingActive,
    isAgeSortingActive,
    isClaimAgeSortingActive,
    isAmountSortingActive,
    isAdvisorSortingActive,
    isVinSortingActive,
    isTechSortingActive,
    isApprovedByDealerSortingActive,
    isLastCommentSortingActive,
    isAuditTrailSortingActive,
    isMyBackUpFilterActive,
    isMyPrimaryFilterActive,
    hasAttachmentSortingActive,
    isClaimStatusSortingActive,
    isTicketPrioritySortingActive,
    isScheduleAccountsFilterActive,
    isAssignToDealerFilterActive,
    isAssignToOEMFilterActive,
    isAssignToWarrCloudFilterActive,
    sortOptions,
    sortSelectValue,
    scheduleAgeOptions,
    selectedAgeFilter,
    handleUpdateFilterClick,
    handleUpdateSortingClick,
    handleUpdateSortSelectClick,
    handleOrderSortSelectClick,
    handleClearAllFiltersClick,
    handleOpenFiltersPopup,
    handleCloseFiltersPopup,
    handleScheduleAgeFilterChange,
    handleUpdateScheduleSelectClick,
    selectKey,
    filtersReset,
    isSelectAllTicketsChecked,
    handleSelectAllTicketsClick,
    setIsSelectAllTicketsChecked,
  };
};

export default useTicketsRefinement;
